import { useEffect, useState } from "react";
import { Check, Globe2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const MOBILE_QUERY = "(max-width: 767px)";

export function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(MOBILE_QUERY);
    if (!mq) return undefined;
    const on = () => setMobile(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return mobile;
}

let activeCache = null;
let activePromise = null;
export const resetActiveLocations = () => { activeCache = null; activePromise = null; };

// Active locations (with `counts_for_coverage`); one shared request, fail-soft. `null` = not loaded yet.
export function useActiveLocations(enabled) {
  const [list, setList] = useState(activeCache);
  useEffect(() => {
    if (!enabled) return undefined;
    if (activeCache) { setList(activeCache); return undefined; }
    let alive = true;
    if (!activePromise) {
      activePromise = api.get("/locations").then(({ data }) => { activeCache = data || []; return activeCache; })
        .catch(() => { activePromise = null; return []; });
    }
    activePromise.then((data) => { if (alive) setList(data); });
    return () => { alive = false; };
  }, [enabled]);
  return list;
}

const OptionList = ({ options, isCurrent, onPick, busy, testId }) => (
  <ul role="listbox" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-options`}
    className="divide-y divide-tw max-h-[60vh] min-h-0 flex-1 overflow-y-auto overscroll-contain">
    {options.map((o) => {
      const current = isCurrent(o);
      const Icon = o.key === "all" ? Globe2 : MapPin;
      return (
        <li key={o.key}>
          <button type="button" role="option" aria-selected={current} disabled={busy} onClick={() => onPick(o)}
            data-testid={`${testId}-option-${o.key}`} data-current={current ? "true" : "false"}
            className={`tw-press w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors duration-150 hover:bg-white/5 disabled:opacity-50 ${current ? "text-tw-yellow" : "text-white"}`}>
            <Icon size={14} strokeWidth={2.5} className="shrink-0" />
            <span className="flex-1 min-w-0 break-words">{o.label}</span>
            {current && <Check size={14} strokeWidth={3} className="shrink-0" />}
          </button>
        </li>
      );
    })}
  </ul>
);

// Round L — bottom sheet on mobile, popover on desktop; same options either way.
export function ScopePicker({ open, onOpenChange, trigger, title, options, isCurrent = () => false, onPick, busy, testId }) {
  const mobile = useIsMobile();
  const list = <OptionList options={options} isCurrent={isCurrent} onPick={onPick} busy={busy} testId={testId} />;
  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-sheet`}
          className="bg-tw-surface border-tw text-white rounded-none p-0 pb-6">
          <SheetHeader className="px-4 pt-4 pb-2 text-left">
            <SheetTitle className="font-mono text-[10px] tracking-[0.3em] uppercase text-tw-muted">{title}</SheetTitle>
            <SheetDescription className="sr-only">Kies waar deze memo bij hoort.</SheetDescription>
          </SheetHeader>
          {list}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" side="bottom" collisionPadding={8} onClick={(e) => e.stopPropagation()}
        data-testid={`${testId}-popover`}
        className="w-64 p-0 bg-tw-surface border-tw text-white rounded-none flex flex-col max-h-[min(60vh,var(--radix-popover-content-available-height))]">
        <div className="px-4 pt-3 pb-2 font-mono text-[10px] tracking-[0.3em] uppercase text-tw-muted">{title}</div>
        {list}
      </PopoverContent>
    </Popover>
  );
}
